import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { LogService } from 'src/app/services/common/log.service';
import { isPlatform } from '@ionic/angular';
import { SubscriberLoginResp } from '../interface/Subscriber/SubscriberLoginResp';
import { CookieService } from 'ngx-cookie-service';
import { Preferences } from '@capacitor/preferences';
import { SignalrService } from 'src/app/services/signalr.service';
import { AppService } from 'src/app/services/app.service';

interface FinancialYear {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  ErrorMessage: string;
  loginForm: FormGroup;
  hide = true;
  Username = new FormControl('', [Validators.required, Validators.email]);
  selectedValue: string;
  financialyears: FinancialYear[] = [];
  token: string | undefined;
  companyID = "";
  companyName = "";
  deleteMode = "";
  financialYear = "";
  isDelete = false;
  displayConfirmDelete = 'none';
  subscriberId = null;
  subscriberResp: SubscriberLoginResp;
  subscriberString: string;
  isLoading = false;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private companyService: CompanyService,
    private router: Router,
    private route: ActivatedRoute,
    private logService: LogService,
    private cookieService: CookieService,
    private signalRService: SignalrService,
    private appService: AppService
  ) {}

  async ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      if (params.has('deleteMode') && params.has('companyID') && params.has('financialYear')) {
        this.isDelete = true;
        this.deleteMode = params.get('deleteMode');
        this.companyID = params.get('companyID');
        this.financialYear = params.get('financialYear');
      } else if (params.has('deleteMode') && params.has('companyID')) {
        this.isDelete = true;
        this.deleteMode = params.get('deleteMode');
        this.companyID = params.get('companyID');
        this.financialYear = params.get('financialYear');
      } else if (params.has('companyID') && params.has('companyName')) {
        this.isDelete = false;
        this.companyID = params.get('companyID');
        this.companyName = params.get('companyName');
      }
    });

    if(isPlatform('capacitor')) {
      await Preferences.set({ key: 'CompanyID', value: this.companyID });
      if(this.companyName !== '' && this.companyName !== null) {
        await Preferences.set({ key: 'CompanyName', value: this.companyName });
      }
    } else {
      sessionStorage.setItem('CompanyID', this.companyID);
      if(this.companyName !== '' && this.companyName !== null) {
        sessionStorage.setItem('CompanyName', this.companyName);
      }
    }

    this.initForm();
    this.getSubscriberDetails();
    this.GetFinancialYear();
  }

  initForm() {
    this.loginForm = this.formBuilder.group({
      Username: ['', [Validators.required]],
      Password: ['', [Validators.required]]
    });

    this.toggleFinancialYearControl();
  }

  toggleFinancialYearControl(): void {
    if (this.deleteMode === 'financialYear' || !this.isDelete) {
      // Add financialyear control with required validator
      this.loginForm.addControl('FinancialYear', this.formBuilder.control('', Validators.required));
    } else {
      // Remove financialyear control
      this.loginForm.removeControl('FinancialYear');
    }
  }

  async getSubscriberDetails() {
    if (isPlatform('capacitor')) {
      this.subscriberResp = await this.authService.getPreferenceSubscriberDetails();
    } else {
      this.subscriberString = sessionStorage.getItem('subscriber');
      if (!this.subscriberString) {
        this.subscriberString = this.cookieService.get('subscriber');
      }
      this.subscriberResp = this.subscriberString ? JSON.parse(this.subscriberString) : null;
    }

    this.subscriberId = this.subscriberResp?.subscriberID;
  }

  async GetFinancialYear() {
    try {
      const companyId = isPlatform('capacitor') ? (await Preferences.get({ key: 'CompanyID' })).value : sessionStorage.getItem("CompanyID");
      let formGroup = new FormGroup({
        CompanyID: new FormControl(companyId)
      });

      const result = await this.authService.GetFinancialYear(formGroup.value).toPromise();
      const years = result.financialYear.split(",");
      this.financialyears = years.map(year => ({
        value: year,
        viewValue: year.split("/")[2]
      }));
    } catch (ex) {
      this.logService.InsertExceptionLog(ex, "Login", "GetFinancialYear");
      console.log("Error in GetFinancialYear: ", ex);
    }
  }

  async Userlogin() {
    try {
      if (this.loginForm.valid) {
        this.isLoading = true;
        const companyId = isPlatform('capacitor') ? (await Preferences.get({ key: 'CompanyID' })).value : sessionStorage.getItem("CompanyID");
        this.financialYear = this.loginForm.value.FinancialYear;
        const loginData = {
          ...this.loginForm.value,
          CompanyID: companyId
        };

        const result = await this.authService.companyLogin(loginData).toPromise();
        if (result == 1) {
          sessionStorage.setItem('UsernameEmail', loginData.Username);
          
          if(isPlatform('capacitor')) {
            await Preferences.set({ key: 'FinancialYear', value: loginData.FinancialYear });
          } else {
            sessionStorage.setItem('FinancialYear', loginData.FinancialYear);
          }

          const daysLeft = sessionStorage.getItem('daysLeft');
          if (daysLeft == '-1') {
            this.ErrorMessage = "Subscription has Ended. Please renew your package";
          } else {
            if (this.isDelete) {
              this.openConfirm();
            } else {
              this.router.navigate(['admin']);
            }
          }

          this.appService.loadInitialData(+companyId, this.financialYear).subscribe(success => {
            if (success) {
              this.isLoading = false;
              this.signalRService.startConnection(companyId);
            }
          });
        } else {
          this.isLoading = false;
          this.ErrorMessage = "Invalid Username or Password.";
        }
      }
    } catch (ex) {
      this.isLoading = false;
      this.logService.InsertExceptionLog(ex, "Login", "Userlogin");
      console.log(ex);
    }
  }
  

  getErrorMessage() {
    if (this.Username.hasError('required')) {
      return 'You must enter a value';
    }

    return this.Username.hasError('Username') ? 'Not a valid Username' : '';
  }

  // GetFinancialYear() {
  //   try {
  //     this.formGroup = new FormGroup({
  //       CompanyID: new FormControl(sessionStorage.getItem("CompanyID"))
  //     });

  //     if (this.formGroup.valid) {
  //       this.authService.GetFinancialYear(this.formGroup.value).subscribe(
  //         (result) => {
  //           const years = result.financialYear.split(",");
  //           for (var i = 0; i < years.length; i++) {
  //             var year: FinancialYear = {
  //               value: years[i],
  //               viewValue: years[i].split("/")[2]
  //             };

  //             this.financialyears.push(year);
  //           }
  //         }, (err: Error) => {
  //           console.log(err.message);
  //         })
  //     }
  //   }
  //   catch (ex) {
  //     this.logService.InsertExceptionLog(ex,"Login","GetFinancialYear");
  //     console.log(ex);
  //   }
  // }

  logout() {
    try {
      // sessionStorage.removeItem("subscriberID");
      // sessionStorage.removeItem('businessType');
      this.authService.logout();
      this.router.navigate(['subscriber-login']);
    }
    catch (ex) {
      this.logService.InsertExceptionLog(ex,"Login","logout");
      console.log(ex);
    }
  }

  selectCompany() {
    try {
      this.router.navigate(
        ['view-company'],
        { queryParams: { subscriberID: this.subscriberId } }
      )
    } catch (ex) {
      console.log(ex);
    }
  }

  deleteCompany() {
    if (this.deleteMode === "company") {
      this.deleteCompanyById(this.companyID);
    } else if (this.deleteMode === "financialYear") {
      if (this.financialyears.length === 1) {
        this.deleteCompanyById(this.companyID);
      } else {
        this.deleteCompanyByFY(this.companyID, this.financialYear);
      }
    }
  }

  deleteCompanyById(companyId: string) {
    let formGroup = new FormGroup({
      CompanyID: new FormControl(companyId)
    });
    try {
      this.companyService.DeleteCompany(formGroup.value).subscribe(
        () => {
          this.clearSession();
          this.selectCompany();
        }, (err: Error) => {
          this.clearSession();
          console.log(err.message);
        });
    }
    catch (ex) {
      this.clearSession();
      console.log(ex);
    }
  }

  deleteCompanyByFY(companyId: string, financialYear: string) {
    let formGroup = new FormGroup({
      CompanyID: new FormControl(companyId),
      FinancialYear: new FormControl(financialYear)
    });

    try {
      this.companyService.DeleteCompanyByFY(formGroup.value).subscribe(
        () => {
          let fyStartDate = [];
          for (let i = 0; i < this.financialyears.length; i++) {
            if (this.financialyears[i].value === financialYear) {
              this.financialyears.splice(i, 1);
            } else {
              fyStartDate.push(this.financialyears[i].value);
            }
          }

          this.companyService.GetCompanyByID().subscribe(
            (result) => {
              result.fyStartDate = fyStartDate.join(",");
              this.companyService.UpdateCompany(result).subscribe(
                (result) => {
                  if (result === 1) {
                    this.clearSession();
                    this.selectCompany();
                  }
                }, (err: Error) => {
                  this.clearSession();
                  console.log(err.message);
                });
            }, (err: Error) => {
              this.clearSession();
              console.log(err.message);
            });
        }, (err: Error) => {
          this.clearSession();
          console.log(err.message);
        });
    }
    catch (ex) {
      this.clearSession();
      this.logService.InsertExceptionLog(ex,"Login","selectCompany");
      console.log(ex);
    }
  }

  clearSession() {
    if (this.deleteMode === "company") {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('companyID');
      sessionStorage.removeItem('CompanyName');
      sessionStorage.removeItem('RoleID');
      sessionStorage.removeItem('UsernameEmail');
    } else if (this.deleteMode === "financialYear") {
      sessionStorage.removeItem('token');
    }
  }

  confirmDelete() {
    this.deleteCompany();
  }

  closeConfirm() {
    this.displayConfirmDelete = 'none';
    this.clearSession();
  }

  openConfirm() {
    this.displayConfirmDelete = 'block';
  }

  redirectToForgotPassword() {
    this.router.navigate(
      ['forgot-password']
    );
  }

}
